import { useState } from "react";
import { Paper, Box, Typography, Divider, Tabs, Tab, Button, CircularProgress, Alert, Snackbar } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ConfirmationModal from "../../../../components/confirmation-modal";
import { generateButtonSx } from "../../../../utils/elementor-style-override";
import CredentialTable from "./CredentialTable";
import CredentialFormDialog from "./CredentialFormDialog";
import { CATEGORIES, GROUPS } from "./constants";
import useAgentCredentials from "./hooks/useAgentCredentials";

const primaryBlue = "#1D54A2";

const addButtonSx = {
	backgroundColor: `${primaryBlue} !important`,
	color: "#FFFFFF !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#16407F !important", text: "#FFFFFF" }, { bg: "#16407F !important", text: "#FFFFFF" }),
};

const CredentialVault = ({ currentAgent }) => {
	const { credentials, loading, error, createCredential, updateCredential, deleteCredential } = useAgentCredentials(currentAgent?.user_id);
	const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
	const [formOpen, setFormOpen] = useState(false);
	const [editingCredential, setEditingCredential] = useState(null);
	const [pendingDelete, setPendingDelete] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });

	const notifyError = (message) => setSnackbar({ open: true, message, severity: "error" });

	const visibleCredentials = credentials.filter((c) => c.category === activeCategory);

	const openAddDialog = () => {
		setEditingCredential(null);
		setFormOpen(true);
	};

	const openEditDialog = (credential) => {
		setEditingCredential(credential);
		setFormOpen(true);
	};

	const closeDialog = () => {
		setFormOpen(false);
		setEditingCredential(null);
	};

	const handleSubmit = async (payload) => {
		const result = editingCredential ? await updateCredential(editingCredential.id, payload) : await createCredential(payload);

		if (!result.success) {
			notifyError(result.error || "Failed to save credential.");
			return;
		}

		closeDialog();
	};

	const confirmDelete = async () => {
		if (!pendingDelete) return;
		const result = await deleteCredential(pendingDelete.id);
		if (!result.success) {
			notifyError(result.error || "Failed to delete credential.");
		}
		setPendingDelete(null);
	};

	return (
		<Paper variant="outlined" sx={{ borderRadius: 2, width: "100% !important" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					flexWrap: "wrap",
					gap: 1,
					px: 2,
					py: 1.5,
					backgroundColor: "secondary.lightbgcolor",
				}}
			>
				<Typography fontWeight={600} color="text.primary">
					Credential Vault
				</Typography>
				<Button size="small" variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openAddDialog} sx={addButtonSx}>
					Add Credential
				</Button>
			</Box>
			<Divider />

			<Tabs
				value={activeCategory}
				onChange={(_e, value) => setActiveCategory(value)}
				sx={{
					px: 2,
					borderBottom: "1px solid",
					borderColor: "divider",
					minHeight: 0,
					"& .MuiTab-root": {
						fontWeight: 600,
						textTransform: "none !important",
						color: "#4A6A8A !important",
						backgroundColor: "transparent !important",
						border: "none !important",
						minHeight: 0,
						py: 1.5,
						"&:hover": {
							color: `${primaryBlue} !important`,
							backgroundColor: "#EBF5FF !important",
						},
						"&:focus-visible": {
							outline: `2px solid ${primaryBlue}`,
							outlineOffset: -2,
						},
					},
					"& .Mui-selected": {
						color: `${primaryBlue} !important`,
						backgroundColor: "transparent !important",
					},
					"& .MuiTabs-indicator": { backgroundColor: `${primaryBlue} !important`, height: 2 },
				}}
			>
				{CATEGORIES.map((category) => (
					<Tab key={category} value={category} label={category} />
				))}
			</Tabs>

			<Box p={2}>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
						<CircularProgress size={26} sx={{ color: primaryBlue }} />
					</Box>
				) : (
					GROUPS.map((group) => (
						<CredentialTable
							key={group}
							title={group}
							credentials={visibleCredentials.filter((c) => c.group === group)}
							onEdit={openEditDialog}
							onDelete={setPendingDelete}
						/>
					))
				)}
			</Box>

			<CredentialFormDialog open={formOpen} onClose={closeDialog} onSubmit={handleSubmit} initialValues={editingCredential} />

			<ConfirmationModal
				open={!!pendingDelete}
				onClose={() => setPendingDelete(null)}
				onConfirm={confirmDelete}
				title="Delete Credential?"
				description={pendingDelete ? `Are you sure you want to delete the "${pendingDelete.carrier}" credential? This cannot be undone.` : ""}
				confirmText="Yes, Delete"
				cancelText="Cancel"
				confirmColor="#E14B5A"
			/>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Paper>
	);
};

export default CredentialVault;
